import React, { useState } from 'react';
import {
  Typography,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Card,
  CardContent,
  Box
} from '@mui/material';
import { formatCurrency, formatNumber } from './formatters';
import { ProductData } from './types';
import { DrillDownConfig } from './configs';
import EmptyState from '../EmptyState';

interface DrillDownProductsSectionProps {
  produtos: ProductData[];
  title?: string;
  showPagination?: boolean;
  rowsPerPage?: number;
  maxHeight?: string;
  gridSize?: { xs?: number; md?: number };
  showEmptyState?: boolean;
  transactionType: 'compras' | 'vendas';
  config: DrillDownConfig;
}

const DrillDownProductsSection: React.FC<DrillDownProductsSectionProps> = ({
  produtos,
  title,
  showPagination = true,
  rowsPerPage = 5,
  maxHeight = "500px",
  gridSize = { xs: 12 },
  showEmptyState = true,
  transactionType,
  config
}) => {
  const [produtosPage, setProdutosPage] = useState(0);

  // Obter o valor correto baseado no tipo de transação
  const getProductValue = (product: ProductData) => {
    return transactionType === 'compras' ? product.valor_compras : product.valor_vendas;
  };

  // Obter a quantidade de parceiros distintos
  const getDistinctPartners = (product: ProductData) => {
    return transactionType === 'compras' ? product.fornecedores_distintos : product.clientes_distintos;
  };

  // Validar se há dados para renderizar
  if (!produtos || produtos.length === 0) {
    if (!showEmptyState) {
      return null;
    }
    return (
      <Grid item xs={gridSize.xs} md={gridSize.md}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              {title || config.productsSectionTitle}
            </Typography>
            <EmptyState
              icon="📦"
              title="Sem produtos"
              description="Não há produtos disponíveis para o período selecionado"
              height={300}
            />
          </CardContent>
        </Card>
      </Grid>
    );
  }

  // Calcular produtos paginados
  const paginatedProducts = showPagination 
    ? produtos.slice(produtosPage * rowsPerPage, produtosPage * rowsPerPage + rowsPerPage)
    : produtos;

  return (
    <Grid item xs={gridSize.xs} md={gridSize.md}>
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" gutterBottom>
              {title || config.productsSectionTitle}
            </Typography>
            <Chip 
              label={`${produtos.length} produtos`} 
              size="small" 
              color="info"
              variant="outlined"
            />
          </Box>
          
          <TableContainer 
            component={Paper} 
            sx={{ 
              mt: 2, 
              maxHeight: maxHeight,
              '& .MuiTableCell-root': {
                borderBottom: '1px solid',
                borderBottomColor: 'divider'
              }
            }}
          >
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Código</strong></TableCell>
                  <TableCell><strong>Produto</strong></TableCell>
                  <TableCell align="right"><strong>{config.valueLabel}</strong></TableCell>
                  <TableCell align="right"><strong>Quantidade</strong></TableCell>
                  <TableCell align="right"><strong>Preço Médio</strong></TableCell>
                  <TableCell align="right"><strong>{config.partnerPluralLabel}</strong></TableCell>
                  <TableCell align="right"><strong>Documentos</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedProducts.map((produto, index) => (
                  <TableRow key={`${produto.cod_item}-${index}`} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {produto.cod_item}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ maxWidth: 300 }}>
                      <Typography 
                        variant="body2"
                        title={produto.descricao_produto}
                        sx={{ 
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {produto.descricao_produto}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight={500} color="success.main">
                        {formatCurrency(getProductValue(produto) || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(produto.quantidade)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatCurrency(produto.preco_medio)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(getDistinctPartners(produto) || 0)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">
                        {formatNumber(produto.documentos)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            
            {/* Paginação */}
            {showPagination && produtos.length > rowsPerPage && (
              <TablePagination
                component="div"
                count={produtos.length}
                page={produtosPage}
                onPageChange={(_, newPage) => setProdutosPage(newPage)}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[]}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
                sx={{
                  borderTop: '1px solid',
                  borderTopColor: 'divider',
                  mt: 1
                }}
              />
            )}
          </TableContainer>
        </CardContent>
      </Card>
    </Grid>
  );
};

export default DrillDownProductsSection; 